// Turnstile challenge loader + renderer (#10a).
//
// This module is imported dynamically from ui.ts via `import('./challenge/turnstile.ts')`,
// but in IIFE library-mode builds Vite inlines the chunk into the main bundle (dynamic
// imports have no module loader to resolve against). Net widget size impact: ~2 KB gzipped.
//
// The REAL lazy-load is Cloudflare's api.js (~100 KB) — we inject its <script> tag only
// when a challenge actually fires. Reporters who never trigger the rate-limit threshold
// pay zero bytes for Cloudflare's SDK.
//
// Responsibilities:
//   - Inject Cloudflare's turnstile api.js if not already loaded
//   - Render the challenge widget into a DOM node
//   - Return a Promise that resolves with the token or rejects on failure/timeout
//   - Cleanup after resolution so the widget can re-render later
//
// Copy rules (5×5 review): never use "bot", "robot", "suspicious", "verify you are human",
// "prove". Frame as "quick check" to keep the reporter trust signal intact.

// ---- Copy constants (inlined per Round-4 prune) ----

export const CHALLENGE_COPY = {
  heading: 'Quick check — please confirm you\u0027re human.',
  subheading: 'Protected by Cloudflare. No tracking.',
  loadErrorHeading: "We couldn't load the verification check.",
  loadErrorBody: 'Please try again, or refresh the page.',
  retryButton: 'Try again',
} as const;

// ---- Turnstile API surface (what we consume from CF's injected api.js) ----

interface TurnstileGlobal {
  render: (
    element: HTMLElement | string,
    options: {
      sitekey: string;
      action?: string;
      cData?: string;
      callback: (token: string) => void;
      'error-callback'?: (err: string) => void;
      'timeout-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
      size?: 'normal' | 'flexible' | 'compact';
      /**
       * #56: BCP-47 language tag (or 'auto'). Cloudflare matches the
       * challenge UI copy to this. We pass the resolved widget locale so
       * the bot-check speaks the same language as the rest of the widget.
       */
      language?: string;
    },
  ) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
  }
}

const API_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const LOAD_TIMEOUT_MS = 8000;

let apiLoadPromise: Promise<TurnstileGlobal> | null = null;

/** Load Cloudflare's api.js once per page. Subsequent calls return the cached promise. */
function loadTurnstileApi(): Promise<TurnstileGlobal> {
  if (apiLoadPromise) return apiLoadPromise;

  let script: HTMLScriptElement | undefined;
  const loading = new Promise<TurnstileGlobal>((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('turnstile_no_window'));
      return;
    }

    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }

    const timer = setTimeout(() => {
      reject(new Error('turnstile_api_timeout'));
    }, LOAD_TIMEOUT_MS);

    script = document.createElement('script');
    script.src = API_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      clearTimeout(timer);
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        reject(new Error('turnstile_api_no_global'));
      }
    };
    script.onerror = () => {
      clearTimeout(timer);
      reject(new Error('turnstile_api_load_error'));
    };
    document.head.appendChild(script);
  });

  const cached = loading.catch((error) => {
    script?.remove();
    if (apiLoadPromise === cached) apiLoadPromise = null;
    throw error;
  });
  apiLoadPromise = cached;
  return cached;
}

export interface ChallengeRenderInput {
  /** Container to render the Turnstile widget into. Must be attached to the DOM. */
  container: HTMLElement;
  /** Site key from server (returned in challenge_required response). */
  siteKey: string;
  /** Action — must match what server expects (server re-asserts it). */
  action: string;
  /** cdata — workspace_id, server re-asserts it. */
  cdata: string;
  /** Override for dark/light theme. Defaults to 'auto'. */
  theme?: 'light' | 'dark' | 'auto';
  /**
   * #56: BCP-47 language tag pinned to the widget's resolved locale so the
   * Cloudflare bot-check UI speaks the same language as everything else.
   * Defaults to 'auto' (Cloudflare's own browser-language detection).
   */
  language?: string;
  signal?: AbortSignal;
}

export interface ChallengeResult {
  token: string;
}

/**
 * Render the Turnstile challenge inside `container` and resolve with the token
 * when the reporter passes. Rejects on error, timeout, or load failure — caller
 * shows an error state.
 *
 * Cleanup: the widget is removed from the container when the promise settles.
 */
export async function runChallenge(input: ChallengeRenderInput): Promise<ChallengeResult> {
  return new Promise<ChallengeResult>((resolve, reject) => {
    let api: TurnstileGlobal | undefined;
    let widgetId: string | undefined;
    let settled = false;
    let phase: 'load' | 'render' | 'waiting' = 'load';
    let renderReturned = false;
    let sdkCode: number | undefined;
    const startedAt = Date.now();
    const recordCode = (value: unknown) => {
      if ((typeof value === 'string' && /^\d{4,6}$/.test(value)) || typeof value === 'number') {
        const code = Number(value);
        if (Number.isInteger(code) && code >= 1000 && code <= 999999) sdkCode = code;
      }
    };
    const cleanup = () => {
      clearTimeout(timer);
      input.signal?.removeEventListener('abort', aborted);
      if (widgetId && api?.remove) {
        try {
          api.remove(widgetId);
        } catch {
          /* Already removed by Cloudflare. */
        }
        widgetId = undefined;
      }
    };
    const finish = (token?: string, error = 'turnstile_challenge_failed') => {
      if (settled) return;
      settled = true;
      let containerRoot: 'shadow' | 'document' | 'other' = 'other';
      let childCount = 0;
      try {
        const root = input.container.getRootNode();
        containerRoot =
          root instanceof ShadowRoot ? 'shadow' : root instanceof Document ? 'document' : 'other';
        childCount = Math.min(32, input.container.childElementCount);
      } catch {
        /* Diagnostics must never change verification settlement. */
      }
      cleanup();
      if (token && !input.signal?.aborted) resolve({ token });
      else {
        try {
          // biome-ignore lint/suspicious/noConsole: fixed metadata only; never log token/sitekey/cData/error text
          console.warn('oncatch_verification_failed', {
            phase,
            reason: input.signal?.aborted
              ? 'cancelled'
              : error === 'turnstile_challenge_timeout'
                ? 'timeout'
                : 'failed',
            renderReturned,
            containerConnected: input.container.isConnected,
            containerRoot,
            childCount,
            elapsedMs: Math.max(0, Math.min(68_000, Date.now() - startedAt)),
            ...(sdkCode === undefined ? {} : { sdkCode }),
          });
        } catch {
          /* A host may replace console with a throwing implementation. */
        }
        reject(new Error(error));
      }
    };
    const aborted = () => finish(undefined, 'turnstile_challenge_cancelled');
    const timer = setTimeout(() => finish(undefined, 'turnstile_challenge_timeout'), 60_000);
    input.signal?.addEventListener('abort', aborted, { once: true });
    if (input.signal?.aborted) {
      aborted();
      return;
    }
    void loadTurnstileApi().then(
      (loaded) => {
        if (settled) return;
        api = loaded;
        try {
          phase = 'render';
          widgetId = api.render(input.container, {
            sitekey: input.siteKey,
            action: input.action,
            cData: input.cdata,
            theme: input.theme ?? 'auto',
            size: 'flexible',
            language: input.language ?? 'auto',
            callback: (token) => finish(token),
            'error-callback': (code) => {
              recordCode(code);
              finish();
            },
            'timeout-callback': () => finish(undefined, 'turnstile_challenge_timeout'),
          });
          renderReturned = typeof widgetId === 'string' && widgetId.length > 0;
          phase = 'waiting';
          // Some SDKs can synchronously call back before render returns its ID.
          if (settled) cleanup();
        } catch (error: unknown) {
          if (error instanceof Error && 'code' in error) recordCode(error.code);
          finish();
        }
      },
      (error: unknown) =>
        finish(
          undefined,
          error instanceof Error && error.message === 'turnstile_api_timeout'
            ? 'turnstile_api_timeout'
            : 'turnstile_api_load_error',
        ),
    );
  });
}
